import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CalendarCheck, Check, X, Clock, ChevronLeft, ChevronRight, Save, FileBarChart2, Printer, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../api';

const STATUS_OPTIONS = [
  { value: 'present', label: 'Present', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', icon: Check },
  { value: 'absent',  label: 'Absent',  color: 'bg-red-100 text-red-700',     dot: 'bg-red-500',     icon: X },
  { value: 'late',    label: 'Late',    color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500',   icon: Clock },
];

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ── Shared classes hook ────────────────────────────────────────────────────────
function useClasses() {
  const [classes, setClasses] = useState([]);
  useEffect(() => { api.get('/academics/classes').then(r => setClasses(r.data || [])).catch(() => {}); }, []);
  return classes;
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AttendancePage() {
  const [tab, setTab] = useState('mark');
  const classes = useClasses();

  return (
    <div className="p-6 lg:p-8 space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-slate-900">Attendance</h1>
          <p className="text-slate-500 text-sm mt-0.5">Track daily attendance and generate monthly reports</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 gap-1">
        {[
          { id: 'mark', icon: CalendarCheck, label: 'Mark Attendance' },
          { id: 'report', icon: FileBarChart2, label: 'Monthly Report' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px
              ${tab === t.id ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'mark'   && <MarkAttendanceTab classes={classes} />}
      {tab === 'report' && <MonthlyReportTab classes={classes} />}
    </div>
  );
}

// ── Mark Attendance Tab ────────────────────────────────────────────────────────
function MarkAttendanceTab({ classes }) {
  const [selectedClass, setSelectedClass] = useState('');
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (classes.length > 0 && !selectedClass) setSelectedClass(classes[0].id); }, [classes, selectedClass]);

  useEffect(() => {
    if (!selectedClass) return;
    setLoading(true);
    Promise.all([
      api.get(`/students?class_id=${selectedClass}&limit=100`),
      api.get(`/academics/attendance?class_id=${selectedClass}&date=${date}`),
    ]).then(([sr, ar]) => {
      setStudents(sr.data || []);
      const map = {};
      (ar.data || []).forEach(r => { map[r.student_id] = r.status; });
      const newMap = {};
      (sr.data || []).forEach(s => { newMap[s.id] = map[s.id] || 'present'; });
      setAttendance(newMap);
    }).catch(() => toast.error('Failed to load attendance'))
      .finally(() => setLoading(false));
  }, [selectedClass, date]);

  const toggleStatus = (id) => {
    const order = ['present', 'absent', 'late'];
    setAttendance(p => ({ ...p, [id]: order[(order.indexOf(p[id] || 'present') + 1) % 3] }));
  };
  const markAll = (s) => setAttendance(Object.fromEntries(students.map(st => [st.id, s])));

  const save = async () => {
    if (!selectedClass || !students.length) return toast.error('Select a class first');
    setSaving(true);
    try {
      await api.post('/academics/attendance', { date, class_id: selectedClass, records: students.map(s => ({ student_id: s.id, status: attendance[s.id] || 'present' })) });
      toast.success(`Attendance saved for ${students.length} students`);
    } catch { toast.error('Failed to save attendance'); }
    finally { setSaving(false); }
  };

  const counts = { present: 0, absent: 0, late: 0 };
  Object.values(attendance).forEach(s => { if (counts[s] !== undefined) counts[s]++; });
  const rate = students.length ? Math.round((counts.present / students.length) * 100) : 0;

  const changeDate = (d) => { const dt = new Date(date); dt.setDate(dt.getDate() + d); setDate(dt.toISOString().split('T')[0]); };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl p-5 border border-slate-100">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <p className="text-xs font-medium text-slate-500 mb-1.5">Class</p>
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger className="h-10"><SelectValue placeholder="Select class" /></SelectTrigger>
              <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500 mb-1.5">Date</p>
            <div className="flex items-center gap-1 border border-slate-200 rounded-lg h-10">
              <button onClick={() => changeDate(-1)} className="px-2 h-full text-slate-500 hover:text-slate-700"><ChevronLeft className="w-4 h-4" /></button>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} className="text-sm text-slate-800 bg-transparent focus:outline-none px-1" />
              <button onClick={() => changeDate(1)} className="px-2 h-full text-slate-500 hover:text-slate-700"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
        {students.length > 0 && (
          <div className="flex gap-3 mt-4 pt-4 border-t border-slate-100">
            {STATUS_OPTIONS.map(s => (
              <button key={s.value} onClick={() => markAll(s.value)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${s.color} hover:opacity-80`}>
                All {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {students.length > 0 && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Total', value: students.length, cls: 'bg-slate-50 text-slate-700' },
            { label: 'Present', value: counts.present, cls: 'bg-emerald-50 text-emerald-700' },
            { label: 'Absent', value: counts.absent, cls: 'bg-red-50 text-red-700' },
            { label: 'Rate', value: `${rate}%`, cls: rate >= 80 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700' },
          ].map((s, i) => (
            <div key={i} className={`${s.cls} rounded-xl p-4 text-center`}>
              <p className="text-2xl font-outfit font-bold">{s.value}</p>
              <p className="text-xs mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse" />)}</div>
      ) : students.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 py-16 text-center">
          <CalendarCheck className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium text-slate-600">{selectedClass ? 'No students in this class' : 'Select a class to take attendance'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <p className="font-outfit font-semibold text-slate-800">
              {classes.find(c => c.id === selectedClass)?.name} — {students.length} Students
            </p>
            <p className="text-xs text-slate-400">Tap a student to cycle status</p>
          </div>
          <div className="divide-y divide-slate-50">
            {students.map((s, i) => {
              const status = attendance[s.id] || 'present';
              const info = STATUS_OPTIONS.find(o => o.value === status);
              const Icon = info?.icon || Check;
              return (
                <div key={s.id} className="px-6 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#eef1ff] flex items-center justify-center text-[#516EFA] text-xs font-bold flex-shrink-0">{i+1}</div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{s.first_name} {s.last_name}</p>
                      <p className="text-xs text-slate-500">{s.admission_number}</p>
                    </div>
                  </div>
                  <button onClick={() => toggleStatus(s.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${info?.color}`} data-testid={`attendance-${s.id}`}>
                    <Icon className="w-3.5 h-3.5" />{info?.label}
                  </button>
                </div>
              );
            })}
          </div>
          <div className="p-4 border-t border-slate-100">
            <Button onClick={save} disabled={saving} className="w-full bg-[#516EFA] hover:bg-[#4058e8] text-white h-11 font-semibold" data-testid="save-attendance-btn">
              <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : `Save Attendance (${students.length} students)`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Monthly Report Tab ────────────────────────────────────────────────────────
function MonthlyReportTab({ classes }) {
  const now = new Date();
  const [selectedClass, setSelectedClass] = useState('');
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const printRef = useRef(null);

  useEffect(() => { if (classes.length > 0 && !selectedClass) setSelectedClass(classes[0].id); }, [classes, selectedClass]);

  const loadReport = useCallback(async () => {
    if (!selectedClass) return;
    setLoading(true);
    try {
      const res = await api.get(`/academics/attendance/report?class_id=${selectedClass}&year=${year}&month=${month}`);
      setReport(res.data);
    } catch { toast.error('Failed to load attendance report'); }
    finally { setLoading(false); }
  }, [selectedClass, year, month]);

  const printReport = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open('', '_blank', 'width=1200,height=800');
    const schoolDays = report?.school_days || [];
    win.document.write(`
      <html><head><title>Attendance Report — ${report?.class_name} — ${report?.month_name} ${report?.year}</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 10px; margin: 20px; }
        h2 { font-size: 14px; margin: 0 0 4px 0; }
        p { margin: 2px 0; color: #555; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 9px; }
        th { background: #f1f5f9; border: 1px solid #e2e8f0; padding: 4px 3px; text-align: center; font-weight: 600; white-space: nowrap; }
        td { border: 1px solid #e2e8f0; padding: 3px; text-align: center; }
        td.name { text-align: left; white-space: nowrap; font-weight: 500; }
        .P { color: #059669; font-weight: 700; }
        .A { color: #dc2626; font-weight: 700; }
        .L { color: #d97706; font-weight: 700; }
        .rate-good { color: #059669; font-weight: 700; }
        .rate-warn { color: #d97706; font-weight: 700; }
        .summary-row { background: #f8fafc; font-weight: 600; }
        @media print { body { margin: 10px; } }
      </style></head><body>
      <h2>Attendance Register — ${report?.class_name}</h2>
      <p>${report?.month_name} ${report?.year} &nbsp;|&nbsp; Total school days: ${report?.total_school_days}</p>
      <table>
        <thead><tr>
          <th style="text-align:left;min-width:140px">#&nbsp;&nbsp;Student Name</th>
          <th style="min-width:60px">Adm. No</th>
          ${schoolDays.map(d => `<th>${new Date(d).getDate()}</th>`).join('')}
          <th>P</th><th>A</th><th>L</th><th>Rate</th>
        </tr></thead>
        <tbody>
          ${(report?.students || []).map((st, i) => `
            <tr>
              <td class="name">${i+1}. ${st.student_name}</td>
              <td>${st.admission_number || ''}</td>
              ${schoolDays.map(d => {
                const s = st.daily?.[d] || '';
                const sym = s === 'present' ? 'P' : s === 'absent' ? 'A' : s === 'late' ? 'L' : '—';
                return `<td class="${sym}">${sym}</td>`;
              }).join('')}
              <td class="P">${st.present}</td>
              <td class="A">${st.absent}</td>
              <td class="L">${st.late}</td>
              <td class="${st.attendance_rate >= 80 ? 'rate-good' : 'rate-warn'}">${st.attendance_rate != null ? st.attendance_rate + '%' : '—'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      <p style="margin-top:12px;font-size:9px;color:#94a3b8">Generated by EDUVA School Management System</p>
      </body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 500);
  };

  const statusShort = (s) => {
    if (s === 'present') return { label: 'P', cls: 'text-emerald-600 font-bold' };
    if (s === 'absent')  return { label: 'A', cls: 'text-red-500 font-bold' };
    if (s === 'late')    return { label: 'L', cls: 'text-amber-500 font-bold' };
    return { label: '—', cls: 'text-slate-300' };
  };

  const schoolDays = report?.school_days || [];

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="bg-white rounded-2xl p-5 border border-slate-100">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-36">
            <p className="text-xs font-medium text-slate-500 mb-1.5">Class</p>
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger className="h-10"><SelectValue placeholder="Select class" /></SelectTrigger>
              <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500 mb-1.5">Month</p>
            <Select value={String(month)} onValueChange={v => setMonth(Number(v))}>
              <SelectTrigger className="h-10 w-36"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500 mb-1.5">Year</p>
            <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
              <SelectTrigger className="h-10 w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={loadReport} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700 text-white h-10" data-testid="load-report-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load Report'}
          </Button>
          {report && (
            <Button variant="outline" onClick={printReport} className="h-10 gap-2" data-testid="print-report-btn">
              <Printer className="w-4 h-4" /> Download PDF
            </Button>
          )}
        </div>
      </div>

      {/* Report Table */}
      {loading && (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
        </div>
      )}

      {!loading && report && (
        <div ref={printRef} data-testid="attendance-report">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-bold text-slate-900">{report.class_name} — {report.month_name} {report.year}</h3>
              <p className="text-xs text-slate-500 mt-0.5">{report.total_school_days} school days · {report.students?.length || 0} students</p>
            </div>
            {/* Summary badges */}
            <div className="flex gap-2">
              {(() => {
                const s = report.students || [];
                const avgRate = s.length ? Math.round(s.reduce((a, b) => a + (b.attendance_rate || 0), 0) / s.length) : 0;
                return (
                  <span className={`text-sm font-bold px-3 py-1.5 rounded-lg ${avgRate >= 80 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    Avg {avgRate}%
                  </span>
                );
              })()}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-auto">
            <table className="w-full text-xs" style={{ minWidth: `${400 + schoolDays.length * 32}px` }}>
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="sticky left-0 bg-slate-50 text-left px-4 py-3 font-semibold text-slate-600 min-w-44 z-10">Student</th>
                  {schoolDays.map(d => (
                    <th key={d} className="px-1 py-3 font-semibold text-slate-500 text-center min-w-8">
                      {new Date(d).getDate()}
                    </th>
                  ))}
                  <th className="px-2 py-3 font-semibold text-emerald-700 text-center">P</th>
                  <th className="px-2 py-3 font-semibold text-red-500 text-center">A</th>
                  <th className="px-2 py-3 font-semibold text-amber-500 text-center">L</th>
                  <th className="px-3 py-3 font-semibold text-slate-600 text-center min-w-14">Rate</th>
                </tr>
              </thead>
              <tbody>
                {(report.students || []).map((st, i) => (
                  <tr key={st.student_id} className={`border-b border-slate-50 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-indigo-50/30 transition-colors`}>
                    <td className={`sticky left-0 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} px-4 py-2.5 z-10`}>
                      <p className="font-semibold text-slate-800">{st.student_name}</p>
                      <p className="text-[10px] text-slate-400">{st.admission_number}</p>
                    </td>
                    {schoolDays.map(d => {
                      const { label, cls } = statusShort(st.daily?.[d]);
                      return <td key={d} className={`text-center py-2.5 ${cls}`}>{label}</td>;
                    })}
                    <td className="text-center py-2.5 font-bold text-emerald-600">{st.present}</td>
                    <td className="text-center py-2.5 font-bold text-red-500">{st.absent}</td>
                    <td className="text-center py-2.5 font-bold text-amber-500">{st.late}</td>
                    <td className="text-center py-2.5">
                      {st.attendance_rate != null ? (
                        <span className={`font-bold ${st.attendance_rate >= 80 ? 'text-emerald-600' : 'text-amber-500'}`}>
                          {st.attendance_rate}%
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
            <span className="font-bold text-emerald-600">P</span> = Present &nbsp;
            <span className="font-bold text-red-500">A</span> = Absent &nbsp;
            <span className="font-bold text-amber-500">L</span> = Late &nbsp;
            <span className="text-slate-300">—</span> = Not recorded
          </div>
        </div>
      )}

      {!loading && !report && (
        <div className="bg-white rounded-2xl border border-slate-100 py-16 text-center">
          <FileBarChart2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium text-slate-600 mb-1">Select a class and month, then click Load Report</p>
          <p className="text-xs text-slate-400">Monthly attendance grid with PDF download</p>
        </div>
      )}
    </div>
  );
}
